import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useUser } from './user';
import { getCompany, getCompanyEmployees } from 'services/companyApi';
import { ICompany, IEmployeeContracts } from 'types';
import { formatErrorMessage, logError } from 'utils/error';
import theraToast from 'components/generic-components/toast/TheraToast';
import { getTheraContract } from 'utils/transformation';

export type CompanyType = ICompany & {
  employeesAndEmployment: IEmployeeContracts[];
  hasEOREmployees?: boolean;
};

type CompanyContextType = {
  company?: CompanyType;
  mutate: () => Promise<void>;
  isLoading: boolean;
};

// Function to check if at least one IEmployeeContract has an employment of type EOR_Employee
function hasEOREmployee(employeeContracts: IEmployeeContracts[]) {
  return employeeContracts.flatMap((worker) => worker.employments).some((employment) => employment.type === 'EMPLOYEE');
}

const Context = createContext<CompanyContextType>({
  company: undefined,
  mutate: async () => {},
  isLoading: false,
});

const CompanyProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, isLoading: isUserLoading, affiliationsByCompany, currentCompany, roleType, isLoggingOut } = useUser();
  const companyId = useMemo(() => {
    if (isUserLoading) {
      return null;
    }
    if (currentCompany?.companyID) {
      return currentCompany.companyID;
    }
    // pick the first company ID from affiliationsByCompany
    return affiliationsByCompany && Object.keys(affiliationsByCompany)[0];
  }, [affiliationsByCompany, currentCompany, isUserLoading]);

  const defaultCompany = affiliationsByCompany?.[companyId]?.company as unknown as CompanyType;
  const [company, setCompany] = useState<CompanyType>(defaultCompany);

  const getCompanyInformation = useCallback(async () => {
    if (!user || !companyId) return;
    setIsLoading(true);
    const isManager = roleType === 'manager';
    try {
      // Fetch company information and employees information in parallel
      // in order to reduce the time but catch errors separately as Promise.all
      // will reject if any of the promises reject.
      if (isManager) {
        await Promise.all([
          getCompanyEmployees(companyId)
            .then((employeesAndEmployment: IEmployeeContracts[]) => {
              const modifiedEmployeesAndEmployment = employeesAndEmployment.map((employee) => ({
                ...employee,
                employments: employee.employments.map((employment) => getTheraContract(employment)),
              }));

              const hasEOREmployees = hasEOREmployee(modifiedEmployeesAndEmployment);
              setCompany((prevCompany) => ({
                ...prevCompany,
                employeesAndEmployment: modifiedEmployeesAndEmployment,
                hasEOREmployees,
              }));
            })
            .catch((error) => {
              const errorMessage = formatErrorMessage(error, 'Unable to fetch employees and employment information.');
              theraToast.error({
                message: errorMessage,
                error: error,
                options: {
                  id: 'fetch-employees-employment',
                },
              });
            }),
          getCompany({ companyID: companyId })
            .then((company) => {
              setCompany((prevCompany) => ({
                ...prevCompany,
                ...company,
              }));
            })
            .catch((error) => {
              logError(error);
            }),
        ]);
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, user, roleType]);

  useEffect(() => {
    // Every company-scoped call takes its id from here, so after a switch the previous company mustn't linger while
    // the selected one loads, or for good if that load fails. The selected company's affiliation record stands in.
    if (companyId) setCompany((prev) => (prev?.companyID === companyId ? prev : defaultCompany));
    // Fetch company info when companyId/roleType change or when user becomes available
    // Use user?.userID instead of user object to avoid loops from user object reference changes
    getCompanyInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, roleType, user?.userID]); // Track user existence without reference loop

  useEffect(() => {
    if (isLoggingOut) {
      setCompany(null);
    }
  }, [isLoggingOut]);

  const exposed = useMemo(
    () => ({ company, mutate: getCompanyInformation, isLoading }),
    [company, getCompanyInformation, isLoading],
  );

  return <Context.Provider value={exposed}>{children}</Context.Provider>;
};

export const useCompany = () => useContext(Context);

export default CompanyProvider;
